import { onSyncStatus } from '../data/sync';
import { shell } from '../app';
import { loadRoute } from '../data/dayRoutes';
import { readStoredSignals } from '../data/signals';
import { getSnapshot, listPlaybook, onStoreChange } from '../data/store';
import type { Snapshot } from '../data/store';
import type { IsoDate } from '../../../shared/types';
import { buildBrief } from '../logic/brief';
import { dayHeading, todayLocal } from '../logic/dates';
import type { Period } from '../logic/home';
import { h, replaceChildren } from '../ui/dom';
import { isDesktop } from '../ui/breakpoint';
import type { View } from '../ui/router';
import { cachedSignalsAt, emptyMirror } from './shared/noLeadsYet';
import { linkBtn } from '../ui/components';
import { briefCard } from './home/briefCard';
import { contractsCard } from './home/contractsCard';
import { performanceCard } from './home/performanceCard';
import { signalsCard } from './home/signalsCard';
import { overviewView } from './overview';

/** Home (D56): the screen the app opens on, after Square's dashboard — today's follow-ups first,
 *  then the numbers, then quick actions. Everything but the email count comes from the mirror. */
const phoneHomeView: View = ({ root }) => {
  shell.setChrome(true);
  const host = h('div', { class: 'page page--home' });
  root.appendChild(host);
  let period: Period = 'week';
  let disposed = false;
  let sequence = 0;
  const signals = signalsCard();
  let perfEl: HTMLElement | null = null;

  const performance = (snap: Snapshot, today: IsoDate): HTMLElement =>
    performanceCard(snap, today, period, (p) => {
      period = p;
      const next = performance(snap, today);
      perfEl?.replaceWith(next);
      perfEl = next;
      next.querySelector<HTMLElement>('.seg__btn[aria-checked="true"]')?.focus();
    });

  const render = async () => {
    if (disposed) return;
    const mine = ++sequence;
    const today = todayLocal(); // per render — a phone left open past midnight must not show yesterday
    shell.setTitle('Home', { subtitle: dayHeading(today) });
    const [snap, stored, playbook] = await Promise.all([
      getSnapshot(),
      readStoredSignals(),
      listPlaybook(),
    ]);
    if (disposed || mine !== sequence) return;
    const brief = buildBrief({
      today,
      leads: snap.leads,
      visits: snap.visits,
      signals: stored?.list.signals ?? [],
      route: loadRoute(today),
      playbook,
    });
    perfEl = performance(snap, today);
    replaceChildren(
      host,
      briefCard({
        followUps: brief.followUps.length,
        calls: brief.calls.length,
        stops: brief.stops.length,
      }),
      snap.leads.length ? null : emptyMirror({ signalsAt: cachedSignalsAt(stored) }),
      signals.el,
      perfEl,
      linkBtn('Visits by day · Reports', '#/dashboard', { variant: 'ghost' }),
      contractsCard(snap.leads, today),

    );
  };

  void render();
  const offSync = onSyncStatus(() => void render());
  const off = onStoreChange(() => void render());
  return () => {
    off();
    offSync();
    disposed = true;
    signals.dispose();
  };
};

/** On a computer the Home is the Overview (D65); on the phone it stays as it is. */
export const homeView: View = (ctx) => (isDesktop() ? overviewView(ctx) : phoneHomeView(ctx));
